import { sortPostStyles } from "@/lib/post-style-order";
import {
  GeneratePostRequestPostStyle,
  GeneratePostRequestSpacingStyle,
  GeneratePostRequestHookStyle,
  GeneratePostRequestPerspective,
  GeneratePostRequestHumorLevel,
  GeneratePostRequestAudience,
  GeneratePostRequestEmojiUsage,
} from "@workspace/api-client-react";

interface PlatformDesc {
  postStyle?: string;
  hook?: string;
  spacing?: string;
  humor?: string;
  audience?: string;
  emoji?: string;
}

type StyleSuggestion = {
  hook?: GeneratePostRequestHookStyle;
  spacing?: GeneratePostRequestSpacingStyle;
  humor?: GeneratePostRequestHumorLevel;
};

interface WritingStyleOptionsProps {
  platform: string;
  platformDesc: PlatformDesc;
  currentIdea: string;
  currentSuggestions: StyleSuggestion;
  postStyle: GeneratePostRequestPostStyle | undefined;
  setPostStyle: (v: GeneratePostRequestPostStyle | undefined) => void;
  hookStyle: GeneratePostRequestHookStyle | undefined;
  setHookStyle: (v: GeneratePostRequestHookStyle | undefined) => void;
  spacingStyle: GeneratePostRequestSpacingStyle | undefined;
  setSpacingStyle: (v: GeneratePostRequestSpacingStyle | undefined) => void;
  perspective: GeneratePostRequestPerspective | undefined;
  setPerspective: (v: GeneratePostRequestPerspective | undefined) => void;
  humorLevel: GeneratePostRequestHumorLevel | undefined;
  setHumorLevel: (v: GeneratePostRequestHumorLevel | undefined) => void;
  audience: GeneratePostRequestAudience | undefined;
  setAudience: (v: GeneratePostRequestAudience | undefined) => void;
  emojiUsage: GeneratePostRequestEmojiUsage | undefined;
  setEmojiUsage: (v: GeneratePostRequestEmojiUsage | undefined) => void;
}

export function WritingStyleOptions({
  platform,
  platformDesc,
  currentIdea,
  currentSuggestions,
  postStyle,
  setPostStyle,
  hookStyle,
  setHookStyle,
  spacingStyle,
  setSpacingStyle,
  perspective,
  setPerspective,
  humorLevel,
  setHumorLevel,
  audience,
  setAudience,
  emojiUsage,
  setEmojiUsage,
}: WritingStyleOptionsProps) {
  const isTwitter = platform === "twitter";
  const isLinkedIn = platform === "linkedin";

  return (
    <>
      {/* Post Style */}
      <div>
        <div className="flex items-center justify-between mb-1">
          <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Post Style</label>
          {postStyle && <button onClick={() => setPostStyle(undefined)} className="text-[10px] text-muted-foreground hover:text-foreground underline transition-colors">Clear</button>}
        </div>
        {platformDesc.postStyle && <p className="text-[10px] text-muted-foreground/70 mb-2 leading-relaxed">{platformDesc.postStyle}</p>}
        <div className="grid grid-cols-2 gap-1.5">
          {sortPostStyles(
            [
              { value: "storytelling", label: "Storytelling", desc: "Narrative arc" },
              { value: "factual",      label: "Factual",      desc: "Data-driven" },
              { value: "listicle",     label: "Listicle",     desc: "List format" },
              { value: "hot-take",     label: "Hot Take",     desc: "Contrarian" },
              { value: "question",     label: "Question",     desc: "Hook & answer" },
              { value: "how-to",       label: "How-To",       desc: "Step-by-step" },
              { value: "personal",     label: "Personal",     desc: "Introspective" },
              { value: "industry",     label: "Insight",      desc: "Expert lens" },
            ] as { value: GeneratePostRequestPostStyle; label: string; desc: string }[],
            currentIdea
          ).map((s) => (
            <button
              key={s.value}
              onClick={() => setPostStyle(postStyle === s.value ? undefined : s.value)}
              className={`flex flex-col items-start px-3 py-2 rounded-xl text-left border transition-all ${
                postStyle === s.value
                  ? "bg-primary/10 border-primary/40 text-primary"
                  : "bg-background border-border text-muted-foreground hover:border-primary/30 hover:text-foreground"
              }`}
            >
              <span className="text-xs font-semibold">{s.label}</span>
              <span className="text-[10px] opacity-70">{s.desc}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Hook Style */}
      <div>
        <div className="flex items-center justify-between mb-1">
          <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Opening Hook</label>
          {hookStyle && <button onClick={() => setHookStyle(undefined)} className="text-[10px] text-muted-foreground hover:text-foreground underline transition-colors">Clear</button>}
        </div>
        {platformDesc.hook && <p className="text-[10px] text-muted-foreground/70 mb-2 leading-relaxed">{platformDesc.hook}</p>}
        <div className="flex flex-wrap gap-1.5">
          {(
            [
              { value: "bold-statement", label: "Bold statement" },
              { value: "question",       label: "Question" },
              { value: "statistic",      label: "Statistic" },
              { value: "anecdote",       label: "Anecdote" },
            ] as { value: GeneratePostRequestHookStyle; label: string }[]
          ).map((h) => {
            const isSuggested = !hookStyle && currentSuggestions.hook === h.value;
            return (
              <button
                key={h.value}
                onClick={() => setHookStyle(hookStyle === h.value ? undefined : h.value)}
                className={`relative px-3 py-1.5 rounded-xl text-xs font-medium border transition-all ${
                  hookStyle === h.value
                    ? "bg-primary/10 border-primary/40 text-primary"
                    : isSuggested
                    ? "bg-primary/5 border-primary/25 text-foreground"
                    : "bg-background border-border text-muted-foreground hover:border-primary/30 hover:text-foreground"
                }`}
              >
                {h.label}
                {isSuggested && (
                  <span className="absolute -top-1.5 -right-1 px-1 py-0.5 rounded text-[8px] font-bold bg-primary/20 text-primary leading-none">Suggested</span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Spacing */}
      <div>
        <div className="flex items-center justify-between mb-1">
          <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Spacing</label>
          {spacingStyle && <button onClick={() => setSpacingStyle(undefined)} className="text-[10px] text-muted-foreground hover:text-foreground underline transition-colors">Clear</button>}
        </div>
        {platformDesc.spacing && <p className="text-[10px] text-muted-foreground/70 mb-2 leading-relaxed">{platformDesc.spacing}</p>}
        <div className="flex gap-1.5">
          {(
            [
              { value: "tight",  label: "Tight",  desc: "Dense paragraphs" },
              { value: "airy",   label: "Airy",   desc: "Room to breathe" },
              { value: "punchy", label: "Punchy", desc: "One thought/line" },
            ] as { value: GeneratePostRequestSpacingStyle; label: string; desc: string }[]
          ).map((s) => {
            const isSuggested = !spacingStyle && currentSuggestions.spacing === s.value;
            return (
              <button
                key={s.value}
                onClick={() => setSpacingStyle(spacingStyle === s.value ? undefined : s.value)}
                className={`relative flex-1 flex flex-col items-center px-2 py-2 rounded-xl text-center border transition-all ${
                  spacingStyle === s.value
                    ? "bg-primary/10 border-primary/40 text-primary"
                    : isSuggested
                    ? "bg-primary/5 border-primary/25 text-foreground"
                    : "bg-background border-border text-muted-foreground hover:border-primary/30 hover:text-foreground"
                }`}
              >
                <span className="text-xs font-semibold">{s.label}</span>
                <span className="text-[10px] opacity-60 mt-0.5">{s.desc}</span>
                {isSuggested && (
                  <span className="absolute -top-1.5 -right-1 px-1 py-0.5 rounded text-[8px] font-bold bg-primary/20 text-primary leading-none">Suggested</span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Perspective */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Perspective</label>
          {perspective && <button onClick={() => setPerspective(undefined)} className="text-[10px] text-muted-foreground hover:text-foreground underline transition-colors">Clear</button>}
        </div>
        <div className="flex gap-1.5">
          {(
            [
              { value: "first-person",  label: "I / Me",  desc: "Personal" },
              { value: "second-person", label: "You",     desc: "Direct to reader" },
              { value: "collective",    label: "We / Us", desc: "Community" },
            ] as { value: GeneratePostRequestPerspective; label: string; desc: string }[]
          ).map((p) => (
            <button
              key={p.value}
              onClick={() => setPerspective(perspective === p.value ? undefined : p.value)}
              className={`flex-1 flex flex-col items-center px-2 py-2 rounded-xl text-center border transition-all ${
                perspective === p.value
                  ? "bg-primary/10 border-primary/40 text-primary"
                  : "bg-background border-border text-muted-foreground hover:border-primary/30 hover:text-foreground"
              }`}
            >
              <span className="text-xs font-semibold">{p.label}</span>
              <span className="text-[10px] opacity-60 mt-0.5">{p.desc}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Humor Level */}
      <div>
        <div className="flex items-center justify-between mb-1">
          <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Humor</label>
          {humorLevel && <button onClick={() => setHumorLevel(undefined)} className="text-[10px] text-muted-foreground hover:text-foreground underline transition-colors">Clear</button>}
        </div>
        {platformDesc.humor && <p className="text-[10px] text-muted-foreground/70 mb-2 leading-relaxed">{platformDesc.humor}</p>}
        <div className="flex flex-wrap gap-1.5">
          {(
            [
              { value: "none",    label: "None",    desc: "Serious" },
              { value: "dry-wit", label: "Dry wit", desc: "Deadpan" },
              { value: "witty",   label: "Witty",   desc: "Clever" },
              { value: "playful", label: "Playful", desc: "Fun" },
            ] as { value: GeneratePostRequestHumorLevel; label: string; desc: string }[]
          ).map((h) => {
            const isSuggested = !humorLevel && currentSuggestions.humor === h.value;
            return (
              <button
                key={h.value}
                onClick={() => setHumorLevel(humorLevel === h.value ? undefined : h.value)}
                className={`relative flex-1 flex flex-col items-center px-2 py-2 rounded-xl text-center border transition-all ${
                  humorLevel === h.value
                    ? "bg-primary/10 border-primary/40 text-primary"
                    : isSuggested
                    ? "bg-primary/5 border-primary/25 text-foreground"
                    : "bg-background border-border text-muted-foreground hover:border-primary/30 hover:text-foreground"
                }`}
              >
                <span className="text-xs font-semibold">{h.label}</span>
                <span className="text-[10px] opacity-60 mt-0.5">{h.desc}</span>
                {isSuggested && (
                  <span className="absolute -top-1.5 -right-1 px-1 py-0.5 rounded text-[8px] font-bold bg-primary/20 text-primary leading-none">Suggested</span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Audience */}
      <div>
        <div className="flex items-center justify-between mb-1">
          <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Target Audience</label>
          {audience && <button onClick={() => setAudience(undefined)} className="text-[10px] text-muted-foreground hover:text-foreground underline transition-colors">Clear</button>}
        </div>
        {platformDesc.audience && <p className="text-[10px] text-muted-foreground/70 mb-2 leading-relaxed">{platformDesc.audience}</p>}
        <div className="grid grid-cols-2 gap-1.5">
          {(
            [
              { value: "general",      label: "General",      desc: "Everyone" },
              { value: "professional", label: "Professional", desc: "Peers / industry" },
              { value: "executive",    label: "Executive",    desc: "C-suite / founders" },
              { value: "friends",      label: "Community",    desc: "Close followers" },
            ] as { value: GeneratePostRequestAudience; label: string; desc: string }[]
          ).map((a) => {
            const isDefault = !audience && (isLinkedIn && a.value === "professional");
            return (
              <button
                key={a.value}
                onClick={() => setAudience(audience === a.value ? undefined : a.value)}
                className={`relative flex flex-col items-start px-3 py-2 rounded-xl text-left border transition-all ${
                  audience === a.value
                    ? "bg-primary/10 border-primary/40 text-primary"
                    : isDefault
                    ? "bg-primary/5 border-primary/20 text-foreground"
                    : "bg-background border-border text-muted-foreground hover:border-primary/30 hover:text-foreground"
                }`}
              >
                <span className="text-xs font-semibold">{a.label}</span>
                <span className="text-[10px] opacity-70">{a.desc}</span>
                {isDefault && (
                  <span className="absolute -top-1.5 -right-1 px-1 py-0.5 rounded text-[8px] font-bold bg-primary/20 text-primary leading-none">Default</span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Emoji Usage */}
      <div>
        <div className="flex items-center justify-between mb-1">
          <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Emoji Usage</label>
          {emojiUsage && <button onClick={() => setEmojiUsage(undefined)} className="text-[10px] text-muted-foreground hover:text-foreground underline transition-colors">Clear</button>}
        </div>
        {platformDesc.emoji && <p className="text-[10px] text-muted-foreground/70 mb-2 leading-relaxed">{platformDesc.emoji}</p>}
        <div className="flex gap-1.5">
          {(
            [
              { value: "off",        label: "Off",        desc: "Zero" },
              { value: "minimal",    label: "Minimal",    desc: "1–2 max" },
              { value: "natural",    label: "Natural",    desc: "As needed" },
              { value: "expressive", label: "Expressive", desc: "Frequent" },
            ] as { value: GeneratePostRequestEmojiUsage; label: string; desc: string }[]
          ).map((e) => {
            const isDefault = !emojiUsage && (
              (isTwitter && e.value === "natural") ||
              (isLinkedIn && e.value === "minimal")
            );
            return (
              <button
                key={e.value}
                onClick={() => setEmojiUsage(emojiUsage === e.value ? undefined : e.value)}
                className={`relative flex-1 flex flex-col items-center px-2 py-2 rounded-xl text-center border transition-all ${
                  emojiUsage === e.value
                    ? "bg-primary/10 border-primary/40 text-primary"
                    : isDefault
                    ? "bg-primary/5 border-primary/20 text-foreground"
                    : "bg-background border-border text-muted-foreground hover:border-primary/30 hover:text-foreground"
                }`}
              >
                <span className="text-xs font-semibold">{e.label}</span>
                <span className="text-[10px] opacity-60 mt-0.5">{e.desc}</span>
                {isDefault && (
                  <span className="absolute -top-1.5 -right-1 px-1 py-0.5 rounded text-[8px] font-bold bg-primary/20 text-primary leading-none">Default</span>
                )}
              </button>
            );
          })}
        </div>
      </div>
    </>
  );
}
